import React, { useState, useEffect, useRef } from 'react';
import { Container, Card, Row, Col, ListGroup, Form, Button, Alert, Modal } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { PERMISOS, etiquetaRol } from '../auth/roles';
import { usuarioService } from '../services/usuarioService';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import AvatarUsuario from '../components/ui/AvatarUsuario';

// Traducción legible de cada permiso de auth/roles.js para mostrar en Mi Perfil.
const DESCRIPCION_PERMISOS = {
    'clientes.ver': 'Ver listado y detalle de clientes',
    'clientes.editar': 'Crear y editar clientes',
    'tramites.ver': 'Ver listado y detalle de trámites',
    'tramites.editar': 'Crear y editar trámites',
    'art.ver': 'Acceder al área ART',
    'art.editar': 'Editar trámites ART',
    'rti.ver': 'Acceder al área RTI',
    'rti.editar': 'Editar trámites RTI',
    'recepcion.ver': 'Acceder al módulo de Recepción',
    'general.ver': 'Ver Médicos y Gastos',
    'agenda.ver': 'Ver y gestionar la Agenda',
    'reportes.ver': 'Ver reportes',
    'herramientas.ver': 'Usar herramientas (correos, calculadora de aportes, feedback)',
    'herramientas.expedientes': 'Seguimiento de Expedientes ANSES',
    'datosMaestros.ver': 'Ver datos maestros',
    'datosMaestros.editar': 'Editar datos maestros',
    'datosMaestros.carpetas.ver': 'Ver y gestionar Carpetas (Datos Maestros)',
    'datosMaestros.carpetas.editar': 'Crear, editar y borrar Carpetas (Datos Maestros)',
    'turnero.config': 'Configurar el turnero',
};

// Recorta al centro y reduce a un cuadrado de `lado` px: la foto queda en pocos KB y no hace
// falta almacenamiento de archivos aparte (se guarda como data URL en el usuario).
const redimensionarFoto = (archivo, lado = 200) => new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onerror = () => rechazar(new Error('No se pudo leer la imagen.'));
    lector.onload = () => {
        const img = new Image();
        img.onerror = () => rechazar(new Error('El archivo no es una imagen válida.'));
        img.onload = () => {
            const corte = Math.min(img.width, img.height);
            const canvas = document.createElement('canvas');
            canvas.width = lado;
            canvas.height = lado;
            canvas.getContext('2d').drawImage(img, (img.width - corte) / 2, (img.height - corte) / 2, corte, corte, 0, 0, lado, lado);
            resolver(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.src = lector.result;
    };
    lector.readAsDataURL(archivo);
});

const MiPerfilPage = () => {
    const { user, logout, actualizarUsuario } = useAuth();
    const navigate = useNavigate();

    const [perfil, setPerfil] = useState({
        nombre: '',
        email: ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
    const [modalAbierto, setModalAbierto] = useState(null); // 'seguridad' | 'permisos' | 'foto' | null
    const [fotoPendiente, setFotoPendiente] = useState(null); // data URL elegida, aún sin guardar
    const [subiendoFoto, setSubiendoFoto] = useState(false);
    const inputFotoRef = useRef(null);

    useEffect(() => {
        if (user) {
            setPerfil({
                nombre: user.nombre || '',
                email: user.email || ''
            });
        }
    }, [user]);

    const handlePerfilChange = (e) => {
        const { name, value } = e.target;
        setPerfil(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdatePerfil = async (e) => {
        e.preventDefault();
        try {
            await usuarioService.updateProfile(user.id, perfil);
            actualizarUsuario({ nombre: perfil.nombre, email: perfil.email });
            setMensaje({ tipo: 'success', texto: 'Perfil actualizado correctamente.' });
            setIsEditing(false);
        } catch (error) {
            console.error(error);
            setMensaje({ tipo: 'danger', texto: 'Error al actualizar el perfil.' });
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        if (user) {
            setPerfil({
                nombre: user.nombre || '',
                email: user.email || ''
            });
        }
    };

    const guardarFoto = async (fotoPerfil) => {
        setSubiendoFoto(true);
        try {
            await usuarioService.updateProfile(user.id, { fotoPerfil });
            actualizarUsuario({ fotoPerfil: fotoPerfil || null });
            setMensaje({ tipo: 'success', texto: fotoPerfil ? 'Foto de perfil actualizada.' : 'Foto de perfil eliminada.' });
            setFotoPendiente(null);
            setModalAbierto(null);
        } catch (error) {
            console.error(error);
            setMensaje({ tipo: 'danger', texto: 'No se pudo guardar la foto de perfil.' });
        } finally {
            setSubiendoFoto(false);
        }
    };

    const handleElegirFoto = async (e) => {
        const archivo = e.target.files?.[0];
        e.target.value = '';
        if (!archivo) return;
        if (!archivo.type.startsWith('image/')) {
            setMensaje({ tipo: 'danger', texto: 'El archivo elegido no es una imagen.' });
            return;
        }
        try {
            setFotoPendiente(await redimensionarFoto(archivo));
        } catch (error) {
            setMensaje({ tipo: 'danger', texto: error.message });
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setMensaje({ tipo: 'danger', texto: 'Las contraseñas nuevas no coinciden.' });
            return;
        }

        try {
            await usuarioService.changePassword(user.id, {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            setMensaje({ tipo: 'success', texto: 'Contraseña cambiada exitosamente. Por favor inicie sesión nuevamente.' });
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });

            setTimeout(() => {
                logout();
                navigate('/login');
            }, 2000);
        } catch (error) {
            console.error(error);
             setMensaje({ tipo: 'danger', texto: error.response?.data?.message || 'Error al cambiar la contraseña. Verifique su contraseña actual.' });
        }
    };

    if (!user) {
        return (
            <Container className="py-5 text-center">
                <p className="text-muted">No hay información de usuario disponible</p>
            </Container>
        );
    }

    // ADMIN y PROGRAMADOR tienen '*' (acceso total): se muestran todos los permisos conocidos.
    const permisosDelRol = PERMISOS[user.rol]?.includes('*')
        ? Object.keys(DESCRIPCION_PERMISOS)
        : (PERMISOS[user.rol] || []);

    const permisos = permisosDelRol.map((p) => DESCRIPCION_PERMISOS[p]).filter(Boolean);

    return (
        <div className="area-admin">
            <Container className="py-4">
                {mensaje.texto && (
                    <Alert variant={mensaje.tipo} onClose={() => setMensaje({ tipo: '', texto: '' })} dismissible>
                        {mensaje.texto}
                    </Alert>
                )}

                <PageHeader
                    variante="plano"
                    icono="person-gear"
                    titulo="Mi Perfil"
                    descripcion="Información de tu cuenta y permisos"
                />

                <div className="text-center my-4">
                    <button
                        type="button"
                        className="border-0 bg-transparent p-0 position-relative d-inline-block"
                        style={{ cursor: 'zoom-in', borderRadius: '50%' }}
                        title="Ver o cambiar foto"
                        onClick={() => { setFotoPendiente(null); setModalAbierto('foto'); }}
                    >
                        <AvatarUsuario nombre={user.nombre} foto={user.fotoPerfil} size={150} style={{ border: '4px solid #fff', boxShadow: '0 4px 16px rgba(13,110,253,0.25)' }} />
                    </button>
                    <h4 className="fw-bold mt-3 mb-1">{user.nombre}</h4>
                    <div className="text-muted small">{etiquetaRol(user.rol)} · ID: {user.id}</div>
                </div>

                <Card className="shadow-sm border-0 rounded-4">
                    <Card.Header className="bg-light border-0 py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <h5 className="mb-0">
                            <i className="bi bi-info-circle me-2 text-primary"></i>
                            Información de la Cuenta
                        </h5>
                        <div className="d-flex flex-wrap gap-2">
                            {!isEditing && (
                                <Button variant="outline-primary" size="sm" onClick={() => setIsEditing(true)}>
                                    <i className="bi bi-pencil me-2"></i>Modificar
                                </Button>
                            )}
                            <Button variant="outline-primary" size="sm" onClick={() => setModalAbierto('seguridad')}>
                                <i className="bi bi-shield-lock me-2"></i>Seguridad
                            </Button>
                            <Button variant="outline-primary" size="sm" onClick={() => setModalAbierto('permisos')}>
                                <i className="bi bi-key-fill me-2"></i>Permisos y Accesos
                            </Button>
                        </div>
                    </Card.Header>
                    <Card.Body className="p-4">
                            {isEditing ? (
                                <Form onSubmit={handleUpdatePerfil}>
                                    <Row className="g-3">
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label>Nombre</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="nombre"
                                                    value={perfil.nombre}
                                                    onChange={handlePerfilChange}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label>Email</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={perfil.email}
                                                    onChange={handlePerfilChange}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={12} className="d-flex justify-content-end gap-2 mt-3">
                                            <Button variant="secondary" onClick={handleCancelEdit}>Cancelar</Button>
                                            <Button variant="primary" type="submit">Guardar Cambios</Button>
                                        </Col>
                                    </Row>
                                </Form>
                            ) : (
                                <Row className="g-4">
                                    <Col md={6}>
                                        <div className="d-flex align-items-start">
                                            <i className="bi bi-person-fill me-3 text-primary" style={{ fontSize: '1.5rem' }}></i>
                                            <div>
                                                <small className="text-muted d-block">Nombre completo</small>
                                                <strong className="fs-6">{user.nombre}</strong>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="d-flex align-items-start">
                                            <i className="bi bi-envelope-fill me-3 text-success" style={{ fontSize: '1.5rem' }}></i>
                                            <div>
                                                <small className="text-muted d-block">Correo electrónico</small>
                                                <strong className="fs-6">{user.email}</strong>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="d-flex align-items-start">
                                            <i className="bi bi-shield-check me-3 text-warning" style={{ fontSize: '1.5rem' }}></i>
                                            <div>
                                                <small className="text-muted d-block">Rol del sistema</small>
                                                <strong className="fs-6">
                                                    {etiquetaRol(user.rol)}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="d-flex align-items-start">
                                            <i className="bi bi-briefcase-fill me-3 text-info" style={{ fontSize: '1.5rem' }}></i>
                                            <div>
                                                <small className="text-muted d-block">Área de trabajo</small>
                                                <strong className="fs-6">{user.rol || '—'}</strong>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                    </Card.Body>
                </Card>

                <Modal show={modalAbierto === 'foto'} onHide={() => { setModalAbierto(null); setFotoPendiente(null); }} centered>
                    <Modal.Header closeButton>
                        <Modal.Title as="h5"><i className="bi bi-camera-fill me-2 text-primary"></i>Foto de perfil</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center p-4">
                        <AvatarUsuario nombre={user.nombre} foto={fotoPendiente || user.fotoPerfil} size={260} />
                        {fotoPendiente && <div className="text-muted small mt-3">Vista previa: todavía no se guardó.</div>}
                        <input ref={inputFotoRef} type="file" accept="image/*" hidden onChange={handleElegirFoto} />
                    </Modal.Body>
                    <Modal.Footer className="justify-content-between">
                        <div>
                            {user.fotoPerfil && !fotoPendiente && (
                                <Button variant="outline-danger" disabled={subiendoFoto} onClick={() => guardarFoto('')}>
                                    <i className="bi bi-trash me-2"></i>Quitar
                                </Button>
                            )}
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="outline-primary" disabled={subiendoFoto} onClick={() => inputFotoRef.current?.click()}>
                                <i className="bi bi-image me-2"></i>Cambiar imagen
                            </Button>
                            <Button variant="primary" disabled={!fotoPendiente || subiendoFoto} onClick={() => guardarFoto(fotoPendiente)}>
                                <i className="bi bi-check-lg me-2"></i>Guardar cambio
                            </Button>
                        </div>
                    </Modal.Footer>
                </Modal>

                <Modal show={modalAbierto === 'seguridad'} onHide={() => setModalAbierto(null)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title as="h5"><i className="bi bi-shield-lock me-2 text-primary"></i>Seguridad</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                            <Form onSubmit={handleChangePassword}>
                                <Row>
                                    <Col md={12} className="mb-3">
                                        <Form.Label>Contraseña Actual</Form.Label>
                                        <Form.Control
                                            type="password"
                                            name="currentPassword"
                                            value={passwordData.currentPassword}
                                            onChange={handlePasswordChange}
                                            required
                                        />
                                    </Col>
                                    <Col md={12} className="mb-3">
                                        <Form.Label>Nueva Contraseña</Form.Label>
                                        <Form.Control
                                            type="password"
                                            name="newPassword"
                                            value={passwordData.newPassword}
                                            onChange={handlePasswordChange}
                                            required
                                            minLength="6"
                                        />
                                    </Col>
                                    <Col md={12} className="mb-3">
                                        <Form.Label>Confirmar</Form.Label>
                                        <Form.Control
                                            type="password"
                                            name="confirmPassword"
                                            value={passwordData.confirmPassword}
                                            onChange={handlePasswordChange}
                                            required
                                            minLength="6"
                                        />
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted small">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Al cambiar la contraseña, se cerrará su sesión actual por seguridad.
                                    </div>
                                    <Button variant="warning" className="text-white" type="submit">
                                        Actualizar Contraseña
                                    </Button>
                                </div>
                            </Form>
                    </Modal.Body>
                </Modal>

                <Modal show={modalAbierto === 'permisos'} onHide={() => setModalAbierto(null)} centered size="lg" scrollable>
                    <Modal.Header closeButton>
                        <Modal.Title as="h5"><i className="bi bi-key-fill me-2 text-primary"></i>Permisos y Accesos</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                            <p className="text-muted mb-3">
                                Como <strong>{etiquetaRol(user.rol)}</strong>,
                                tienes acceso a las siguientes funcionalidades del sistema:
                            </p>
                            <ListGroup variant="flush">
                                {permisos.map((permiso, index) => (
                                    <ListGroup.Item
                                        key={index}
                                        className="border-0 px-0 py-2"
                                    >
                                        <i className="bi bi-check-circle-fill me-2 text-success"></i>
                                        {permiso}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                    </Modal.Body>
                </Modal>
            </Container>
        </div>
    );
};

export default MiPerfilPage;
